import type { MouseEvent, ReactNode } from 'react';
import { Box, Drawer, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconFigmaInfo } from '@/assets/icons';
import { filterSheetStyles } from '@/components/common';

/** Info ikona s objašnjenjem: na webu tooltip na hover/fokus, na mobitelu dodir otvara sheet odozdo
 * (tooltip je na dodir sitan, izlazi preko ruba i teško se zatvara). `label` prima ReactNode da npr.
 * alerti mogu prikazati listu uvjeta; `title` je naslov sheeta (npr. naziv kartice). */
export const PanelInfoIcon = ({
  label,
  width = 240,
  title
}: {
  label: ReactNode;
  width?: number;
  title?: string;
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [opened, setOpened] = useState(false);
  const ariaLabel = title ?? t('common.info');

  if (isMobile) {
    return (
      <>
        <UnstyledButton
          aria-label={ariaLabel}
          onClick={(event: MouseEvent) => {
            event.stopPropagation();
            setOpened(true);
          }}
          style={{
            display: 'flex',
            padding: 8,
            margin: -8,
            color: 'var(--color-text-muted)'
          }}
        >
          <IconFigmaInfo size={15} />
        </UnstyledButton>
        <Drawer
          opened={opened}
          onClose={() => setOpened(false)}
          position="bottom"
          title={
            <Text fz={15} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {ariaLabel}
            </Text>
          }
          styles={filterSheetStyles}
        >
          <Box fz={14} lh={1.5} style={{ color: 'var(--color-text-secondary)' }}>
            {label}
          </Box>
        </Drawer>
      </>
    );
  }

  return (
    <Tooltip
      label={label}
      multiline
      w={width}
      withArrow
      events={{ hover: true, focus: true, touch: false }}
      color="var(--color-bg-surface)"
      styles={{
        tooltip: {
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border-soft)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      <UnstyledButton
        aria-label={ariaLabel}
        style={{ color: 'var(--color-text-muted)', display: 'flex', cursor: 'help' }}
      >
        <IconFigmaInfo size={15} />
      </UnstyledButton>
    </Tooltip>
  );
};

/**
 * Bold uvod (broj uvjeta + opseg filtriranja) + popis uvjeta ispod — isti obrazac kao Board
 * "Ključna upozorenja" tooltip (board.alerts.title na BoardManagementPage). Koristi se kao
 * `label` na PanelInfoIcon-u svuda gdje jedan broj/panel zapravo pokriva više zasebnih pravila.
 */
export const InfoBulletList = ({ intro, items }: { intro: ReactNode; items: ReactNode[] }) => (
  <Stack gap={4}>
    <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
      {intro}
    </Text>
    <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--color-text-secondary)' }}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </Stack>
);
